/**
 * Schedule un callback pendant un moment "idle" du navigateur.
 * Utilisé par les BootProviders pour prefetch des queries sans concurrencer
 * le LCP / la query principale de la page actuellement affichée.
 *
 * - `requestIdleCallback` quand dispo (Chrome, Firefox, Safari récent)
 * - Fallback `setTimeout` pour les vieux Safari
 *
 * Retourne une fonction de cleanup à appeler depuis le useEffect.
 */
export function scheduleIdleTask(
  callback: () => void,
  options: { fallbackDelayMs?: number; timeout?: number } = {},
): () => void {
  const { fallbackDelayMs = 1000, timeout = 2000 } = options
  if (typeof window === 'undefined') {
    return () => {}
  }

  // Cast explicite : TS narrowing avec `in` rend la branche `else` invalide
  // (type never), donc on lit la fonction depuis l'objet directement.
  const ric = (window as Window & {
    requestIdleCallback?: (
      cb: IdleRequestCallback,
      opts?: IdleRequestOptions,
    ) => number
    cancelIdleCallback?: (id: number) => void
  }).requestIdleCallback

  if (typeof ric === 'function') {
    const id = ric(callback, { timeout })
    return () => window.cancelIdleCallback?.(id)
  }

  const timer = window.setTimeout(callback, fallbackDelayMs)
  return () => window.clearTimeout(timer)
}

/**
 * Exécute une liste de tâches en cascade pour ne pas saturer le backend.
 *
 * Pourquoi : le worker uvicorn unique de Callizy + Django ORM sync = ~5-8
 * requêtes parallèles avant goulot. Si on lance 8 prefetch en même temps,
 * la query principale de l'user (en plus) doit attendre en queue. Cascader
 * par petits lots étale la charge.
 *
 * - `initialDelayMs` (2000) : attend pour laisser l'user interagir d'abord.
 * - `batchSize` (2) : lance N tâches en parallèle par lot.
 * - `batchDelayMs` (300) : pause entre 2 lots, laisse le backend respirer.
 *
 * Retourne un cleanup pour annuler les lots restants si l'effet unmount.
 */
export function runStaggered(
  tasks: Array<() => void>,
  options: {
    initialDelayMs?: number
    batchSize?: number
    batchDelayMs?: number
  } = {},
): () => void {
  const { initialDelayMs = 2000, batchSize = 2, batchDelayMs = 300 } = options
  if (typeof window === 'undefined' || tasks.length === 0) {
    return () => {}
  }

  let cancelled = false
  let i = 0
  let timer: number | null = null

  const runBatch = () => {
    if (cancelled) return
    const batch = tasks.slice(i, i + batchSize)
    i += batchSize
    for (const task of batch) {
      try {
        task()
      } catch {
        // Une tâche défaillante ne doit pas casser la cascade.
      }
    }
    if (i < tasks.length) {
      timer = window.setTimeout(runBatch, batchDelayMs)
    }
  }

  timer = window.setTimeout(runBatch, initialDelayMs)

  return () => {
    cancelled = true
    if (timer !== null) window.clearTimeout(timer)
  }
}
